import './App.css';
import * as React from 'react';
import QuestionForm from './components/QuestionForm';
import Layout from './components/Layout';
import ComponentResults from './pages/ComponentResults';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import LogoutPage from './pages/LogoutPage';
import ConfirmationPage from './pages/ConfirmationPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SchoolProfilePage from './pages/SchoolProfilePage';
import UserProfilePage from './pages/UserProfilePage';
import AdminPage from './pages/AdminPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import SecuritySettingsPage from './pages/SecuritySettingsPage';
import Configurator from './pages/Configurator';
import ReviewBook from './pages/ReviewBook';
import Checkout from './pages/Checkout';
import InfoCenter from './pages/InfoCenter';
import AGBPage from './pages/AGBPage';
import WiderrufsbelehrungPage from './pages/WiderrufsbelehrungPage';
import { useNavigate, Route, Routes, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { ProfileProvider, ProfileContext } from './context/ProfileContext';
import { useSession } from './context/SessionContext';
import setupInterceptors from './utils/setupInterceptors';
import { jwtDecode } from 'jwt-decode';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import axiosInstance from './axiosConfig';

function App() {
  // This is necessary to pass back the data from QuestionForm component
  const [data, setData] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [userProfile, setUserProfile] = React.useState(null);
  const [userRole, setUserRole] = React.useState(null);
  const { sessionExpiredMessage, setSessionExpiredMessage } = useSession();
  const navigate = useNavigate();

  React.useEffect(() => {
    setupInterceptors(setSessionExpiredMessage, navigate);
  }, [navigate]);

  React.useEffect(() => {
    const refreshDocsToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const response = await axiosInstance.get('/auth/generate-docs-token', {
          headers: { Authorization: `${token}` },
        });

        if (response.data.docsToken) {
          localStorage.setItem('docsToken', response.data.docsToken);
        }
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Docs-Tokens:', error);
      }
    };

    refreshDocsToken();
  }, []);

  const theme = createTheme({
    palette: {
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#9c27b0',
        light: '#ba68c8',
        dark: '#7b1fa2',
        contrastText: '#ffffff',
      },
      success: {
        main: '#4caf50',
        light: '#4caf50',
        dark: '#1b5e20',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#ed6c02',
        light: '#ff9800',
        dark: '#e65100',
        contrastText: '#000000',
      },
      info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
        contrastText: '#ffffff',
      },
      error: {
        main: '#d32f2f',
        light: '#ef5350',
        dark: '#c62828',
        contrastText: '#ffffff',
      },
      custom1: { main: '#ff9800', light: '#ff980038' }, // Orange für 5-7
      custom2: { main: '#9c27b0', light: '#9c27b038' }, // Lila für 8-9
      custom3: { main: '#3f51b5', light: '#3f51b538' }, // Blau für 10-11
      custom4: { main: '#795548', light: '#79554838' }  // Braun für 12-13
    },
  });

  const ProtectedRoute = ({ children, fallback = <SignInPage />, requiredRole }) => {
    const [loading, setLoading] = React.useState(true);
    const token = localStorage.getItem('authToken');
    const navigate = useNavigate();

    React.useEffect(() => {
      const attemptRefresh = async () => {
        if (!token) {
          try {
            const refreshResponse = await axiosInstance.get('/auth/refresh');
            const newAccessToken = refreshResponse.data.accessToken;
            localStorage.setItem('authToken', newAccessToken);

            try {
              const docsResponse = await axiosInstance.get('/auth/generate-docs-token', {
                headers: { Authorization: `${newAccessToken}` },
              });

              if (docsResponse.data.docsToken) {
                localStorage.setItem('docsToken', docsResponse.data.docsToken);
              }
            } catch (error) {
              console.error('Fehler beim Aktualisieren des Docs-Tokens nach Refresh:', error);
            }

            setLoading(false);
          } catch (error) {
            console.error("Failed to refresh token:", error);
            localStorage.removeItem('authToken');
            navigate('/pages/login');
          }
        } else {
          setLoading(false);
        }
      };

      attemptRefresh();
    }, [token, navigate]);

    if (loading) {
      return <div>Lade...</div>; // Verhindert, dass die Route wechselt, bevor der Refresh-Versuch abgeschlossen ist
    }

    try {
      const decodedToken = jwtDecode(localStorage.getItem('authToken'));
      const userRole = decodedToken.role;

      if (requiredRole && userRole !== requiredRole) {
        return navigate("/pages/unauthorized");
      }

      return children;
    } catch (error) {
      console.error("Error decoding token or invalid token:", error);
      return navigate("/pages/login");
    }
  };

  return (
      <ThemeProvider theme={theme}>
        <ProfileProvider>
          <ProfileContext.Consumer>
            {({ userProfile }) => (
              <>
                <meta name="viewport" content="initial-scale=1, width=device-width" />
                <Box>
                  {sessionExpiredMessage && (
                    <div style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', color: '#d32f2f', padding: '10px', textAlign: 'center', }}>
                      {sessionExpiredMessage}
                    </div>
                  )}
                  <Routes>
                    <Route path="/" element={<Layout />}>
                      <Route
                        index
                        element={
                          <ProtectedRoute fallback={<SignInPage
                            setIsAuthenticated={setIsAuthenticated}
                            setSessionExpiredMessage={setSessionExpiredMessage}
                            setUserProfile={setUserProfile}
                            setUserRole={setUserRole}
                          />}>
                            <UserProfilePage />
                          </ProtectedRoute>
                        }
                      />
                    <Route path="/pages/login" element={<SignInPage setIsAuthenticated={setIsAuthenticated} setSessionExpiredMessage={setSessionExpiredMessage} setUserProfile={setUserProfile} setUserRole={setUserRole} />} />
                    <Route path="/pages/logout" element={<LogoutPage />} />
                    <Route path="/pages/register" element={<SignUpPage />} />
                    <Route path="/pages/confirmation" element={<ConfirmationPage />} />
                    <Route path="/pages/verify-email" element={<EmailVerificationPage />} />
                    <Route path="/pages/reset-password/:token" element={<ResetPasswordPage />} />
                    <Route path="/pages/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
                    <Route path="/pages/user-profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
                    <Route path="/pages/school-profile" element={<ProtectedRoute><SchoolProfilePage /></ProtectedRoute>} />
                    <Route path="/pages/admin" element={<ProtectedRoute requiredRole="admin"><AdminPage /></ProtectedRoute>} />
                    <Route path="/pages/configurator" element={
                      (userProfile?.schoolId && typeof userProfile.schoolId === 'string' && userProfile.schoolId.trim() !== '' && userProfile?.schoolVerified && (userProfile?.schoolProfileComplete || userProfile?.adminProfileComplete))
                        ? (<ProtectedRoute><Configurator /></ProtectedRoute>)
                        : (<Navigate to="/pages/unauthorized" replace />)
                    } />
                    <Route path="/pages/QuestionForm" element={<ProtectedRoute><QuestionForm setData={setData} /></ProtectedRoute>} />
                    <Route path="/pages/ComponentResults" element={<ProtectedRoute><ComponentResults data={data} /></ProtectedRoute>} />
                    <Route path="/pages/review" element={<ProtectedRoute><ReviewBook /></ProtectedRoute>} />
                    <Route path="/pages/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/pages/info" element={<ProtectedRoute><InfoCenter /></ProtectedRoute>} />
                    <Route path="/pages/unauthorized" element={<UnauthorizedPage />} />
                    <Route path="/pages/agb" element={<AGBPage />} />
                    <Route path="/pages/widerruf" element={<WiderrufsbelehrungPage />} />
                    </Route>
                  </Routes>
                </Box>
              </>
            )}
          </ProfileContext.Consumer>
        </ProfileProvider>
      </ThemeProvider>
  );
}

export default App;
